import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

import { User } from "./models/user";

export type CodeDispatchMethod = "EMAIL" | "SMS";

export interface CustomerSignup {
  name: string;
  email: string;
  phoneNumber: string;
  cpf: string;
  password: string;
}

interface CustomerAuthResponse {
  user: User;
  csrfToken: string;
}

interface CustomerCsrfResponse {
  csrfToken: string;
}

const CSRF_ERROR = "CSRF_TOKEN_INVALID";
let csrfToken = "";
let csrfRequest: Promise<string> | null = null;

const customerHttp = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
});

export class CustomerApiError extends Error {
  constructor(
    message: string,
    public status = 0,
    public fields: Record<string, string> = {},
    public code = "",
    public details: unknown = null
  ) {
    super(message);
    this.name = "CustomerApiError";
  }
}

function isNonGet(config: AxiosRequestConfig) {
  return Boolean(config.method && config.method.toUpperCase() !== "GET");
}

function rememberCsrfToken(value: unknown) {
  if (!value || typeof value !== "object") return;
  const nextToken = (value as Partial<CustomerCsrfResponse>).csrfToken;
  if (typeof nextToken === "string" && nextToken) csrfToken = nextToken;
}

async function ensureCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  if (!csrfRequest) {
    csrfRequest = customerHttp
      .get<CustomerCsrfResponse>("auth/customer/csrf")
      .then(({ data }) => {
        rememberCsrfToken(data);
        if (!csrfToken) throw new CustomerApiError("Não foi possível proteger a solicitação.");

        return csrfToken;
      })
      .finally(() => {
        csrfRequest = null;
      });
  }

  return csrfRequest;
}

function isCsrfError(error: unknown) {
  return axios.isAxiosError(error) && error.response?.data?.message === CSRF_ERROR;
}

function parseError(error: unknown): CustomerApiError | null {
  if (!axios.isAxiosError(error)) return null;

  const response = error.response;
  const message = response?.data?.message;
  const code = response?.data?.code;
  const details = response?.data?.details ?? null;
  const fields: Record<string, string> = {};
  const properties = response?.data?.properties;
  if (Array.isArray(properties)) {
    properties.forEach(({ property, errorMessages }) => {
      if (typeof property === "string" && Array.isArray(errorMessages)) {
        fields[property] = errorMessages.filter((entry) => typeof entry === "string").join(". ");
      }
    });
  }

  return new CustomerApiError(
    typeof message === "string"
      ? message
      : response?.status === 429
      ? "Muitas tentativas. Aguarde alguns segundos e tente novamente."
      : "Não foi possível conectar. Verifique sua conexão e tente novamente.",
    response?.status,
    fields,
    typeof code === "string" ? code : "",
    details
  );
}

export async function customerRequest<T>(
  config: AxiosRequestConfig,
  mayRefreshCsrf = true
): Promise<T> {
  try {
    const requestCsrfToken = isNonGet(config) ? await ensureCsrfToken() : "";
    const response: AxiosResponse<T> = await customerHttp.request<T>({
      ...config,
      headers: {
        ...(config.headers ?? {}),
        ...(requestCsrfToken ? { "X-Tirss-CSRF": requestCsrfToken } : {}),
      },
    });
    rememberCsrfToken(response.data);

    return response.data;
  } catch (error) {
    if (mayRefreshCsrf && isCsrfError(error)) {
      csrfToken = "";

      return customerRequest<T>(config, false);
    }
    throw parseError(error) ?? error;
  }
}

export const customerAuthApi = {
  login: (email: string, password: string, remember: boolean) =>
    customerRequest<CustomerAuthResponse>({
      method: "POST",
      url: "auth/customer/login",
      data: { email, password, remember },
    }),
  google: (credential: string, remember: boolean) =>
    customerRequest<CustomerAuthResponse>({
      method: "POST",
      url: "auth/customer/google",
      data: { credential, remember },
    }),
  signup: (data: CustomerSignup) =>
    customerRequest<CustomerAuthResponse>({ method: "POST", url: "auth/customer/signup", data }),
  completeProfile: (data: { cpf?: string; phoneNumber?: string }) =>
    customerRequest<{ cpf: string | null; phoneNumber: string | null }>({
      method: "PATCH",
      url: "customer/account/complete-profile",
      data,
    }),
  session: () =>
    customerRequest<CustomerAuthResponse>({ method: "GET", url: "auth/customer/session" }),
  logout: async () => {
    try {
      await customerRequest<void>({ method: "POST", url: "auth/customer/logout" });
    } finally {
      csrfToken = "";
    }
  },
  requestConfirmation: (codeDispatchMethod: CodeDispatchMethod) =>
    customerRequest<void>({
      method: "POST",
      url: "auth/customer/request_signup_confirmation_code",
      data: { codeDispatchMethod },
    }),
  confirm: (signupConfirmationCode: string) =>
    customerRequest<void>({
      method: "POST",
      url: "auth/customer/validate_signup_confirmation_code",
      data: { signupConfirmationCode },
    }),
  recover: (codeDispatchMethod: CodeDispatchMethod, contact: string) =>
    customerRequest<void>({
      method: "POST",
      url: "auth/customer/request_password_reset",
      data: {
        codeDispatchMethod,
        ...(codeDispatchMethod === "EMAIL" ? { email: contact } : { phoneNumber: contact }),
      },
    }),
  validateResetCode: (passwordResetCode: string) =>
    customerRequest<void>({
      method: "POST",
      url: "auth/customer/validate_password_reset_code",
      data: { passwordResetCode },
    }),
  resetPassword: (passwordResetCode: string, password: string) =>
    customerRequest<void>({
      method: "POST",
      url: "auth/customer/reset_password",
      data: { passwordResetCode, password },
    }),
};
